import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatCr, timeAgo, GATE_LABELS, formatSignalSource } from "@/lib/utils-ros";
import {
  AlertTriangle, ArrowRight, Building2, Check, ChevronDown, Clock3, Loader2,
  Package, Plus, Radio, RotateCcw, Search, Sparkles, Target, Ticket, X, Zap
} from "lucide-react";
import { toast } from "sonner";
import { unwrapCompaniesPage } from "@/lib/companies";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const TASK_GROUPS = [
  { key: "overdue", label: "Overdue", color: "#DC2626", tint: "rgba(220, 38, 38, 0.05)" },
  { key: "today", label: "Due Today", color: "#D97706", tint: "rgba(217, 119, 6, 0.05)" },
  { key: "upcoming", label: "Upcoming", color: "#65A30D", tint: "rgba(101, 163, 13, 0.05)" },
];

const TASK_ENTITY_GROUPS = ["Universe", "Signals", "Leads", "Opportunities", "Orders"];

export default function Home() {
  const [data, setData] = useState(null);
  const [taskData, setTaskData] = useState({ buckets: {}, counts: {} });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", due: "", priority: "medium", entityKey: "" });
  const [taskEntityOptions, setTaskEntityOptions] = useState([]);
  const [taskEntitiesLoaded, setTaskEntitiesLoaded] = useState(false);
  const [taskEntitiesLoading, setTaskEntitiesLoading] = useState(false);

  async function loadDashboard() {
    setLoading(true);
    try {
      const [tiles, tasks] = await Promise.all([
        api.get("/home/tiles"),
        api.get("/tasks/buckets"),
      ]);
      setData(tiles.data);
      setTaskData(tasks.data || { buckets: {}, counts: {} });
    } catch {
      toast.error("Could not load the dashboard");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDashboard(); }, []);

  useEffect(() => {
    if (!adding || taskEntitiesLoaded || taskEntitiesLoading) return;

    async function loadTaskEntities() {
      setTaskEntitiesLoading(true);
      const results = await Promise.allSettled([
        api.get("/companies", { params: { limit: 200 } }),
        api.get("/signals?limit=500"),
        api.get("/leads"),
        api.get("/opportunities"),
        api.get("/orders"),
      ]);
      const companies = results[0].status === "fulfilled"
        ? unwrapCompaniesPage(results[0].value.data).items
        : [];
      const signals = results[1].status === "fulfilled" ? (results[1].value.data || []) : [];
      const leads = results[2].status === "fulfilled" ? (results[2].value.data || []) : [];
      const opportunities = results[3].status === "fulfilled" ? (results[3].value.data || []) : [];
      const orders = results[4].status === "fulfilled" ? (results[4].value.data || []) : [];
      setTaskEntityOptions([
        ...companies.map(company => ({
          key: `company:${company.id}`,
          group: "Universe",
          label: company.name,
          detail: company.industry || "Customer",
          icon: Building2,
          payload: { company_id: company.id, source_type: "universe_action", source_id: company.id },
        })),
        ...signals.map(signal => ({
          key: `signal:${signal.id}`,
          group: "Signals",
          label: signal.company?.name || signal.intent || "Signal",
          detail: signal.intent || signal.signal_type || "Signal",
          icon: Radio,
          payload: { company_id: signal.company_id, signal_id: signal.id, source_type: "signal_action", source_id: signal.id },
        })),
        ...leads.map(lead => ({
          key: `lead:${lead.id}`,
          group: "Leads",
          label: lead.company?.name || lead.contact_name || "Lead",
          detail: lead.contact_name ? `${lead.contact_name} · ${lead.status}` : lead.status || "Lead",
          icon: Zap,
          payload: { company_id: lead.company_id, lead_id: lead.id, source_type: "lead_action", source_id: lead.id },
        })),
        ...opportunities.map(opportunity => ({
          key: `opportunity:${opportunity.id}`,
          group: "Opportunities",
          label: opportunity.company?.name || opportunity.name || "Opportunity",
          detail: opportunity.name || "Opportunity",
          icon: Target,
          payload: { company_id: opportunity.company_id, opportunity_id: opportunity.id, source_type: "opp_action", source_id: opportunity.id },
        })),
        ...orders.map(order => ({
          key: `order:${order.id}`,
          group: "Orders",
          label: order.company?.name || order.po_number || "Order",
          detail: order.po_number ? `PO ${order.po_number}` : "Order",
          icon: Package,
          payload: { company_id: order.company_id, opportunity_id: order.opportunity_id, source_type: "order_action", source_id: order.id },
        })),
      ]);
      setTaskEntitiesLoaded(true);
      setTaskEntitiesLoading(false);
    }

    loadTaskEntities();
  }, [adding, taskEntitiesLoaded, taskEntitiesLoading]);

  async function completeTask(id) {
    try {
      await api.patch(`/tasks/${id}`, { status: "completed" });
      setTaskData(current => {
        const nextBuckets = { ...current.buckets };
        for (const group of TASK_GROUPS) {
          nextBuckets[group.key] = (nextBuckets[group.key] || []).filter(t => t.id !== id);
        }
        const nextCounts = { ...current.counts };
        for (const group of TASK_GROUPS) {
          if ((current.buckets[group.key] || []).some(t => t.id === id)) {
            nextCounts[group.key] = Math.max(0, (nextCounts[group.key] || 0) - 1);
          }
        }
        return { ...current, buckets: nextBuckets, counts: nextCounts };
      });
      toast.success("Task completed");
    } catch {
      toast.error("Could not complete task");
    }
  }

  async function createTask(e) {
    e.preventDefault();
    const title = newTask.title.trim();
    if (!title) return;
    const payload = {
      title,
      priority: newTask.priority,
      task_type: "reminder",
      source_type: "manual",
      ...(taskEntityOptions.find(option => option.key === newTask.entityKey)?.payload || {}),
    };
    if (newTask.due) payload.due_date = new Date(`${newTask.due}T23:59:59`).toISOString();
    setSaving(true);
    try {
      await api.post("/tasks", payload);
      setNewTask({ title: "", due: "", priority: "medium", entityKey: "" });
      setAdding(false);
      toast.success("Task added");
      await loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add task");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-7 md:py-10" data-testid="home-page">
      <section className="-mx-4 md:-mx-6 -mt-7 md:-mt-10 px-4 md:px-6 pt-7 md:pt-10 pb-8 md:pb-10 mb-0 bg-[linear-gradient(to_bottom,#E6F5FB_0%,#E6F5FB_48%,#FFFFFF_82%,#FFFFFF_100%)]">
        <h1 className="font-display text-3xl md:text-4xl tracking-tight">Good day. Where do we begin?</h1>
        <Link
          to="/chat"
          data-testid="chat-with-niq"
          className="group mt-6 min-h-14 md:min-h-16 px-4 md:px-5 rounded-xl bg-white border border-black/15 shadow-[0_8px_24px_rgba(10,42,67,0.06)] hover:border-[var(--saarv-blue)]/60 hover:shadow-[0_10px_30px_rgba(0,162,212,0.10)] transition-all flex items-center gap-3"
        >
          <span className="w-8 h-8 rounded-lg bg-[var(--saarv-blue-soft)] text-[var(--saarv-blue-strong)] flex items-center justify-center shrink-0">
            <Sparkles size={15} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-display text-[15px] md:text-base">Ask Niq</span>
            <span className="block text-[11px] text-[var(--text-muted)] mt-0.5 truncate">Log meetings, create leads, and capture pipeline updates in plain language</span>
          </span>
          <ArrowRight size={16} className="text-[var(--saarv-blue)] group-hover:translate-x-1 transition-transform" />
        </Link>
      </section>

      {loading && !data ? <DashboardSkeleton /> : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 items-stretch" data-testid="home-dashboard">
          <section className="lg:col-span-4 lg:row-span-2 rounded-2xl overflow-hidden bg-white border border-black/10 shadow-[0_12px_34px_rgba(10,42,67,0.08)] flex flex-col min-h-[640px]" data-testid="dashboard-todo">
            <div className="bg-[var(--saarv-blue-strong)] text-[#FFFFFF] px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl text-[#FFFFFF]">To Do</h2>
              </div>
              <button
                type="button"
                onClick={() => setAdding(v => !v)}
                data-testid="dashboard-add-task"
                aria-label="Add a task"
                className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-[#FFFFFF] flex items-center justify-center transition-colors"
              >
                {adding ? <X size={18} /> : <Plus size={20} />}
              </button>
            </div>

            {adding && (
              <form onSubmit={createTask} className="p-4 border-b border-black/10 bg-[var(--saarv-blue-soft)]" data-testid="dashboard-add-task-form">
                <input
                  autoFocus
                  value={newTask.title}
                  onChange={e => setNewTask(s => ({ ...s, title: e.target.value }))}
                  placeholder="What needs to get done?"
                  className="w-full h-9 px-3 rounded-md border border-black/15 bg-white text-[12px] outline-none focus:border-[var(--saarv-blue)]"
                  data-testid="dashboard-task-title"
                />
                <div className="mt-2">
                  <DashboardCustomerPicker
                    options={taskEntityOptions}
                    loading={taskEntitiesLoading}
                    value={newTask.entityKey}
                    onChange={(entityKey) => setNewTask(s => ({ ...s, entityKey }))}
                  />
                </div>
                <div className="grid grid-cols-[1fr_auto_auto] gap-2 mt-2">
                  <input
                    type="date"
                    value={newTask.due}
                    onChange={e => setNewTask(s => ({ ...s, due: e.target.value }))}
                    className="min-w-0 h-8 px-2 rounded-md border border-black/15 bg-white text-[11px] outline-none"
                    data-testid="dashboard-task-due"
                  />
                  <select
                    value={newTask.priority}
                    onChange={e => setNewTask(s => ({ ...s, priority: e.target.value }))}
                    className="h-8 px-2 rounded-md border border-black/15 bg-white text-[11px] capitalize outline-none"
                    data-testid="dashboard-task-priority"
                  >
                    {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <button
                    type="submit"
                    disabled={!newTask.title.trim() || saving}
                    className="h-8 px-3 rounded-md bg-[var(--saarv-blue-strong)] text-[#FFFFFF] text-[11px] disabled:opacity-50 flex items-center gap-1"
                    data-testid="dashboard-task-save"
                  >
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
                  </button>
                </div>
              </form>
            )}

            <div className="flex-1 p-4 space-y-3">
              {TASK_GROUPS.map(group => (
                <TaskGroup
                  key={group.key}
                  group={group}
                  tasks={taskData.buckets?.[group.key] || []}
                  count={taskData.counts?.[group.key] || 0}
                  onComplete={completeTask}
                />
              ))}
            </div>
          </section>

          <ListCard title="At-Risk Deals" icon={AlertTriangle} to="/opportunities" className="lg:col-span-4" empty="No active deals are at risk." testId="dashboard-at-risk">
            {(data?.needs_attention?.top || []).map(o => (
              <ListItem key={o.id} to={`/opportunities/${o.id}`} title={o.name} meta={`${formatCr(o.value_cr)} · ${GATE_LABELS[o.current_gate] || o.current_gate}`} badge={o.health?.replace("_", " ")} />
            ))}
          </ListCard>

          <ListCard title="Strong Signals" icon={Radio} to="/signals" className="lg:col-span-4" empty="No high-intent activity right now." testId="dashboard-signals">
            {(data?.strong_signals?.top || []).map(s => (
              <ListItem key={s.id} to="/signals" title={s.intent} meta={`${s.company?.name || formatSignalSource(s.source)} · ${timeAgo(s.created_at)}`} badge={`${s.strength}`} />
            ))}
          </ListCard>

          <ListCard title="Priority Leads" icon={Zap} to="/leads" className="lg:col-span-4" empty="No high-priority leads right now." testId="dashboard-priority-leads">
            {(data?.hot_leads?.top || []).map(l => (
              <ListItem key={l.id} to="/leads" title={l.company?.name || l.contact_name || "Unnamed lead"} meta={[l.contact_name, l.product_interest?.[0]].filter(Boolean).join(" · ") || "Sales lead"} badge={l.urgency} />
            ))}
          </ListCard>

          <MetricCard title="Open Tickets" icon={Ticket} value={data?.open_tickets?.count || 0} description="Unresolved support cases" to="/tickets" className="lg:col-span-2" testId="dashboard-open-tickets" />
          <MetricCard title="Reorders" icon={RotateCcw} value={data?.upcoming_reorders?.count || 0} description="Active renewals and reorders" to="/reorder" className="lg:col-span-2" testId="dashboard-reorders" />
        </div>
      )}
    </div>
  );
}

function DashboardCustomerPicker({ options, loading, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find(option => option.key === value);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter(option =>
      `${option.label} ${option.detail} ${option.group}`.toLowerCase().includes(normalized)
    );
  }, [options, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="dashboard-task-customer"
          className="w-full min-h-9 px-3 rounded-md border border-black/15 bg-white text-left flex items-center justify-between gap-2 hover:border-[var(--saarv-blue)] outline-none"
        >
          <span className="min-w-0">
            <span className={`block text-[11px] truncate ${selected ? "text-[var(--text)]" : "text-[var(--text-dim)]"}`}>
              {selected?.label || "Choose a customer record"}
            </span>
            {selected && <span className="block text-[9px] text-[var(--text-dim)] truncate">{selected.group} · {selected.detail}</span>}
          </span>
          {loading ? <Loader2 size={13} className="animate-spin text-[var(--saarv-blue)] shrink-0" /> : <ChevronDown size={13} className="text-[var(--saarv-blue)] shrink-0" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(90vw,20rem)] rounded-xl border-[#BFD9E5] p-2 shadow-[0_16px_40px_rgba(7,83,154,0.18)]">
        <div className="h-9 rounded-md bg-[#F4FAFD] border border-[#D5E7EF] px-2.5 flex items-center gap-2">
          <Search size={13} className="text-[var(--saarv-blue)]" />
          <input
            autoFocus
            value={query}
            onChange={event => setQuery(event.target.value)}
            data-testid="dashboard-customer-search"
            placeholder="Search customers and records…"
            className="flex-1 min-w-0 bg-transparent outline-none text-[11px]"
          />
        </div>
        <div className="mt-1.5 max-h-64 overflow-y-auto">
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); setQuery(""); }}
            data-testid="dashboard-customer-none"
            className="w-full px-2.5 py-2 rounded-md text-left text-[10px] text-[var(--text-muted)] hover:bg-[var(--saarv-blue-soft)]"
          >
            No customer — personal task
          </button>
          {TASK_ENTITY_GROUPS.map(group => {
            const groupOptions = filtered.filter(option => option.group === group);
            if (groupOptions.length === 0) return null;
            return (
              <div key={group} className="mt-1.5">
                <div className="px-2.5 py-1 text-[9px] uppercase tracking-wider text-[var(--text-dim)]">{group}</div>
                {groupOptions.map(option => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => { onChange(option.key); setOpen(false); setQuery(""); }}
                      data-testid={`dashboard-customer-option-${option.key}`}
                      className="w-full px-2.5 py-2 rounded-md flex items-center gap-2 text-left hover:bg-[var(--saarv-blue-soft)] transition-colors"
                    >
                      <span className="w-7 h-7 rounded-md bg-[#E4F4FA] text-[var(--saarv-blue-strong)] flex items-center justify-center shrink-0"><Icon size={12} /></span>
                      <span className="min-w-0">
                        <span className="block text-[11px] truncate">{option.label}</span>
                        <span className="block text-[9px] text-[var(--text-dim)] truncate">{option.detail}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          {!loading && filtered.length === 0 && <div className="px-3 py-6 text-center text-[10px] text-[var(--text-dim)]">No matching records</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TaskGroup({ group, tasks, count, onComplete }) {
  return (
    <div
      className="rounded-xl border p-3 shadow-[0_7px_18px_rgba(10,42,67,0.10)]"
      style={{ borderColor: `${group.color}40`, background: group.tint }}
      data-testid={`task-group-${group.key}`}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <Clock3 size={13} style={{ color: group.color }} />
        <h3 className="font-display text-[12px]" style={{ color: group.color }}>{group.label}</h3>
        <span className="ml-1 px-1.5 min-w-5 h-5 rounded-full bg-white border border-black/10 text-[10px] font-mono flex items-center justify-center">{count}</span>
        <Link to="/tasks" className="ml-auto text-[10px] font-medium hover:underline" style={{ color: group.color }}>All <ArrowRight size={10} className="inline" /></Link>
      </div>
      <div className="space-y-1">
        {tasks.length === 0 ? (
          <div className="text-[11px] text-[var(--text-dim)] py-2">Nothing here</div>
        ) : tasks.slice(0, 3).map(task => (
          <div key={task.id} className="group/task flex items-start gap-2.5 py-1.5" data-testid={`dashboard-task-${task.id}`}>
            <button type="button" onClick={() => onComplete(task.id)} aria-label={`Complete ${task.title}`} className="w-4 h-4 mt-0.5 rounded border border-black/25 bg-white hover:border-[var(--saarv-blue)] hover:bg-[var(--saarv-blue-soft)] flex items-center justify-center shrink-0">
              <Check size={10} className="opacity-0 group-hover/task:opacity-100 text-[var(--saarv-blue-strong)]" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] leading-snug truncate">{task.title}</div>
              {(task.company?.name || task.opportunity?.name || task.ticket?.subject) && (
                <div className="text-[9px] text-[var(--text-dim)] truncate mt-0.5">{task.company?.name || task.opportunity?.name || task.ticket?.subject}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListCard({ title, icon: Icon, to, className = "", empty, testId, children }) {
  const hasItems = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <section className={`${className} min-h-[260px] rounded-2xl bg-white border border-black/10 border-t-4 border-t-[var(--saarv-blue-strong)] shadow-[0_10px_28px_rgba(10,42,67,0.07)] p-5 flex flex-col`} data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-8 h-8 rounded-lg bg-[var(--saarv-blue-soft)] text-[var(--saarv-blue-strong)] flex items-center justify-center"><Icon size={15} /></span>
        <h2 className="font-display text-[16px]">{title}</h2>
      </div>
      <div className="space-y-1 flex-1">
        {hasItems ? children : <div className="text-[11px] text-[var(--text-dim)] py-6">{empty}</div>}
      </div>
      <Link to={to} className="self-end mt-4 text-[11px] font-medium text-[var(--saarv-blue-strong)] hover:underline inline-flex items-center gap-1">All <ArrowRight size={11} /></Link>
    </section>
  );
}

function ListItem({ to, title, meta, badge }) {
  return (
    <Link to={to} className="group flex items-center gap-3 py-2.5 border-b border-black/[0.06] last:border-0 hover:text-[var(--saarv-blue-strong)] transition-colors">
      <span className="w-1.5 h-1.5 rounded-full bg-[var(--saarv-blue)] shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block text-[12px] truncate">{title}</span>
        <span className="block text-[10px] text-[var(--text-muted)] truncate mt-0.5">{meta}</span>
      </span>
      {badge && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--saarv-blue-soft)] text-[var(--saarv-blue-strong)] shrink-0">{badge}</span>}
      <ArrowRight size={11} className="text-[var(--text-dim)] group-hover:translate-x-0.5 transition-transform shrink-0" />
    </Link>
  );
}

function MetricCard({ title, icon: Icon, value, description, to, className = "", testId }) {
  return (
    <Link to={to} className={`${className} group min-h-[260px] rounded-2xl bg-white border border-black/10 border-t-4 border-t-[var(--saarv-blue-strong)] shadow-[0_10px_28px_rgba(10,42,67,0.07)] p-5 flex flex-col hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(10,42,67,0.11)] transition-all`} data-testid={testId}>
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-[var(--saarv-blue-soft)] text-[var(--saarv-blue-strong)] flex items-center justify-center"><Icon size={15} /></span>
        <h2 className="font-display text-[15px]">{title}</h2>
      </div>
      <div className="mt-auto mb-auto py-5">
        <div className="font-display text-5xl tracking-tight tabular-nums text-[var(--saarv-blue-strong)]">{value}</div>
        <div className="text-[10px] text-[var(--text-muted)] mt-2 leading-snug">{description}</div>
      </div>
      <span className="self-end text-[11px] font-medium text-[var(--saarv-blue-strong)] inline-flex items-center gap-1">See all <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" /></span>
    </Link>
  );
}

function DashboardSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 animate-pulse">
      <div className="lg:col-span-4 h-[640px] rounded-2xl bg-black/[0.04]" />
      <div className="lg:col-span-4 h-[260px] rounded-2xl bg-black/[0.04]" />
      <div className="lg:col-span-4 h-[260px] rounded-2xl bg-black/[0.04]" />
      <div className="lg:col-span-4 h-[260px] rounded-2xl bg-black/[0.04]" />
      <div className="lg:col-span-2 h-[260px] rounded-2xl bg-black/[0.04]" />
      <div className="lg:col-span-2 h-[260px] rounded-2xl bg-black/[0.04]" />
    </div>
  );
}
